import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { sameDates } from "../../src/trip-dates.ts";
import { ApiError, api, type DatesHint, type TripView, type When } from "./api.ts";
import { dayLabel, tripDates } from "./format.ts";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { Button, Icon, SegmentedControl } from "./souvenir.ts";

// The trip's name and dates, first in Trip settings (DESIGN.md, Views, the web's own edits; features/trips-and-
// members.md, "The trip's name and dates"): for anyone in the trip, as through an AI. Its dates are none, rough as a
// number of days, or a first and last day. One write signed "on the web", undoable at once (the trip's notice) and
// from Updates, over the trip as read; new dates say what they do to what's in it (the write's hints): things on
// relative days moving with its first day, things now outside its dates. Trip settings' first card, The trip.

const TITLE_MAX = 200;
const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
type Kind = "none" | "days" | "dates";

// What the form starts from: the trip's dates as one of the three.
function fromDates(dates: When): { kind: Kind; count: string; first: string; last: string } {
  if (dates.precision === "day" && isDate(dates.day))
    return { kind: "dates", count: "", first: dates.day, last: dates.day };
  if (dates.precision === "days" && isDate(dates.start) && isDate(dates.end))
    return { kind: "dates", count: "", first: dates.start, last: dates.end };
  if (dates.precision === "days" && dates.start === "day 1" && /^day \d+$/.test(dates.end))
    return { kind: "days", count: dates.end.slice(4), first: "", last: "" };
  return { kind: "none", count: "", first: "", last: "" };
}

// The trip's dates as its header says them, or how many days, or none yet.
function datesLine(dates: When): string {
  const shown = tripDates(dates);
  if (shown) return shown;
  if (dates.precision === "days" && dates.start === "day 1" && /^day \d+$/.test(dates.end)) {
    const n = Number(dates.end.slice(4));
    return `${n} ${n === 1 ? "day" : "days"}, no dates yet`;
  }
  return "No dates yet";
}

// A hint as people read it: what new dates did to what's in the trip, each thing on its own line with its day.
function Hint({ hint }: { hint: DatesHint }) {
  const SHOWN = 5;
  const lines = hint.titles.map((title, i) => {
    const day = hint.days[i] ?? "";
    const [relative, date] = day.split(", ");
    const when = date ? `${relative}, now ${dayLabel(date)}` : isDate(day) ? dayLabel(day) : day;
    return { key: hint.ids[i] ?? title, title, when };
  });
  const dated = hint.days.some((d) => d.includes(", "));
  return (
    <div className="tm-trip-hint">
      <p className="tm-trip-hint-words">
        {hint.kind === "outside_dates"
          ? "Outside the new dates, left where they are. Move a place or goal from its card (Move to another day), or ask your AI:"
          : dated
            ? "On days of the trip (day 1, day 2…), so they moved with its first day:"
            : "On days of the trip (day 1, day 2…), which have no dates now:"}
      </p>
      <ul>
        {lines.slice(0, lines.length > SHOWN ? SHOWN - 1 : SHOWN).map((l) => (
          <li key={l.key}>
            {l.title} · {l.when}
          </li>
        ))}
        {lines.length > SHOWN && <li>and {lines.length - (SHOWN - 1)} more</li>}
      </ul>
    </div>
  );
}

export function TripDetails({ trip, onChanged }: { trip: TripView; onChanged: () => void }) {
  const readOnly = useReadOnly();
  const [editing, setEditing] = useState(false);
  // What the dates just saved did, said while the trip is as they left it (gone once anything changes it, an Undo
  // among them).
  const [said, setSaid] = useState<{ hints: DatesHint[]; version: number } | null>(null);
  const hints = said && trip.version === said.version ? said.hints : [];
  const shown = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState(false);
  useEffect(() => {
    if (!refocus || editing) return;
    setRefocus(false);
    shown.current?.querySelector<HTMLElement>(".sv-btn")?.focus({ preventScroll: true });
  }, [refocus, editing]);
  return (
    <SettingsGroup id="trip-details-title" title="The trip">
      <div className="tm-settings-part tm-trip-details">
        {editing ? (
          <TripDetailsForm
            trip={trip}
            onChanged={onChanged}
            onClose={(saved) => {
              setEditing(false);
              setRefocus(true);
              if (saved) setSaid(saved);
            }}
          />
        ) : (
          <div className="tm-trip-details-shown" ref={shown}>
            <div className="tm-trip-details-words">
              <p className="body-strong tm-trip-details-name">{trip.title}</p>
              <p className="callout tm-muted tm-trip-details-name">{datesLine(trip.dates)}</p>
            </div>
            {!readOnly && trip.version !== undefined && (
              <Button
                variant="secondary"
                size="sm"
                className="tm-trip-details-edit"
                aria-label="Edit the trip's name and dates"
                onClick={() => {
                  setSaid(null);
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
        )}
        {!editing && hints.length > 0 && (
          <div className="callout tm-booking-note tm-trip-hints" role="status">
            <Icon name="calendar" size={20} className="tm-booking-icon" />
            <div>
              {hints.map((h) => (
                <Hint key={h.kind} hint={h} />
              ))}
            </div>
          </div>
        )}
      </div>
    </SettingsGroup>
  );
}

function TripDetailsForm({
  trip,
  onChanged,
  onClose,
}: {
  trip: TripView;
  onChanged: () => void;
  // Closed, saved or not; saved, with what the new dates did (the write's hints).
  onClose: (saved: { hints: DatesHint[]; version: number } | null) => void;
}) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  // The trip as the form read it: its version is what the change is written over.
  const [base, setBase] = useState({ version: trip.version as number, title: trip.title, dates: trip.dates });
  const start = fromDates(trip.dates);
  const [title, setTitle] = useState(trip.title);
  const [kind, setKind] = useState<Kind>(start.kind);
  const [count, setCount] = useState(start.count);
  const [first, setFirst] = useState(start.first);
  const [last, setLast] = useState(start.last);
  const [problem, setProblem] = useState<{ field: string; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meanwhile, setMeanwhile] = useState(false);
  const [busy, setBusy] = useState(false);
  // Saved, at this version: closed once the trip has loaded with it (or after a while all the same).
  const [saved, setSaved] = useState<{ version: number; hints: DatesHint[] } | null>(null);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, []);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!saved) return;
    if ((trip.version ?? 0) >= saved.version) return close.current(saved);
    const timer = setTimeout(() => close.current(saved), 5000);
    return () => clearTimeout(timer);
  }, [saved, trip.version]);

  // The dates as written, or what's wrong with them.
  const dates = (): When | null | { field: string; message: string } => {
    if (kind === "none") return null;
    if (kind === "days") {
      const n = Number(count);
      if (!Number.isInteger(n) || n < 1 || n > 999) return { field: "count", message: "How many days: 1 or more." };
      return { precision: "days", start: "day 1", end: `day ${n}` };
    }
    if (!first) return { field: "first", message: "The first day, or choose No dates." };
    if (!last || last === first) return { precision: "day", day: first };
    if (last < first) return { field: "last", message: "The last day is before the first." };
    return { precision: "days", start: first, end: last };
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (offline || busy || saved) return;
    const name = title.trim();
    if (!name) {
      setProblem({ field: "title", message: "Give the trip a name." });
      return;
    }
    const when = dates();
    if (when && "field" in when) {
      setProblem(when);
      return;
    }
    setProblem(null);
    setError(null);
    setBusy(true);
    try {
      const { change, version, hints } = await api.tripDetails(trip.id, {
        version: base.version,
        title: name,
        dates: when,
      });
      if (!change) return onClose(null);
      onChanged();
      const renamed = name !== base.title;
      const redated = !sameDates(when, base.dates.precision === "none" ? null : base.dates);
      say({
        change,
        text:
          renamed && redated
            ? "Changed the trip's name and dates"
            : renamed
              ? `Renamed the trip ${name}`
              : "Changed the trip's dates",
      });
      setSaved({ version, hints });
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 409 && err.data["trip"]) {
        const now = err.data["trip"] as { title: string; dates: When; version: number };
        setBase(now);
        setMeanwhile(true);
        onChanged();
        return;
      }
      setError(err instanceof Error ? err.message : "Couldn't save it. Try again.");
    }
  };

  const wrong = (field: string) => (problem?.field === field ? problem.message : null);
  const field = (name: string, label: string, input: ReactNode, hint?: string) => (
    <div className={`sv-field tm-booking-field${wrong(name) ? " is-wrong" : ""}`}>
      <label className="sv-field-label" htmlFor={`${id}-${name}`}>
        {label}
      </label>
      {input}
      {wrong(name) ? (
        <span className="sv-field-hint tm-error">{wrong(name)}</span>
      ) : (
        hint && <span className="sv-field-hint">{hint}</span>
      )}
    </div>
  );
  return (
    <form
      ref={form}
      className="tm-edit-form tm-trip-details-form"
      aria-label="The trip's name and dates"
      noValidate
      onSubmit={save}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onClose(null);
      }}
    >
      {meanwhile && (
        <p className="callout tm-booking-note is-meanwhile" role="alert">
          <Icon name="people" size={20} className="tm-booking-icon" />
          <span>
            Someone changed the trip while this was open, so nothing was saved: it's "{base.title}",{" "}
            {datesLine(base.dates).toLowerCase()} now. Check yours and save again.
          </span>
        </p>
      )}
      {field(
        "title",
        "Name",
        <input
          id={`${id}-title`}
          className="sv-field-input"
          value={title}
          maxLength={TITLE_MAX}
          autoComplete="off"
          aria-invalid={wrong("title") ? true : undefined}
          onChange={(e) => setTitle(e.target.value)}
        />,
      )}
      <fieldset className="tm-trip-details-dates">
        <legend className="sv-field-label">Dates</legend>
        <SegmentedControl
          label="Dates"
          value={kind}
          onChange={(v) => {
            setKind(v as Kind);
            setProblem(null);
          }}
          options={[
            { value: "none", label: "None yet" },
            { value: "days", label: "How long" },
            { value: "dates", label: "Dates" },
          ]}
        />
        {kind === "days" &&
          field(
            "count",
            "How many days",
            <input
              id={`${id}-count`}
              className="sv-field-input tm-trip-details-count"
              inputMode="numeric"
              autoComplete="off"
              value={count}
              aria-invalid={wrong("count") ? true : undefined}
              onChange={(e) => setCount(e.target.value.replace(/\D/g, "").slice(0, 3))}
            />,
            "Things go on day 1, day 2… until the trip has dates.",
          )}
        {kind === "dates" && (
          <div className="tm-booking-pair">
            {field(
              "first",
              "First day",
              <input
                id={`${id}-first`}
                className={`sv-field-input${first ? "" : " is-empty"}`}
                type="date"
                value={first}
                aria-invalid={wrong("first") ? true : undefined}
                onChange={(e) => {
                  setFirst(e.target.value);
                  if (last && e.target.value > last) setLast("");
                }}
              />,
            )}
            {field(
              "last",
              "Last day",
              <input
                id={`${id}-last`}
                className={`sv-field-input${last ? "" : " is-empty"}`}
                type="date"
                value={last}
                min={first || undefined}
                aria-invalid={wrong("last") ? true : undefined}
                onChange={(e) => setLast(e.target.value)}
              />,
            )}
          </div>
        )}
      </fieldset>
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy || saved !== null || offline}>
          {busy || saved ? "Saving…" : "Save"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={() => onClose(null)}>
          Cancel
        </Button>
      </span>
    </form>
  );
}
